import { useEffect, useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/shared/ui/sheet';
import { Switch } from '@/shared/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';
import { CampaignPhoneMockup } from '@/features/campaigns/ui/campaign-phone-mockup';
import { usePlatform } from '../model/demo-context';
import { PushTestForm } from './push-test-form';
import { fillVariables, VARIABLE_SAMPLES, type LocalizedText, type SystemPush } from '../model/types';
import { LocalizedFields, VariableChips } from './platform-fields';

function ruleLabel(push: SystemPush): string | null {
  if (push.rule?.daysBefore) return `${push.rule.daysBefore} dias antes`;
  return null;
}

/** Pushes que o sistema manda sozinho. A linha abre o editor; o switch liga o envio. */
export function SystemPushesTab() {
  const { config, savePush } = usePlatform();
  const [editing, setEditing] = useState<SystemPush | null>(null);

  return (
    <>
      <div className="overflow-hidden rounded-2xl border border-border/60 bg-card">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>Push</TableHead>
                <TableHead className="hidden md:table-cell">Texto</TableHead>
                <TableHead className="hidden sm:table-cell">Regra</TableHead>
                <TableHead className="text-right">Ativo</TableHead>
                <TableHead className="w-8" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {config.pushes.map((push) => {
                const rule = ruleLabel(push);
                return (
                  <TableRow key={push.kind} className="cursor-pointer" onClick={() => setEditing(push)} tabIndex={0}>
                    <TableCell>
                      <p className="text-sm font-medium">{push.label}</p>
                      <p className="text-xs text-muted-foreground">{push.trigger}</p>
                    </TableCell>
                    <TableCell className="hidden max-w-[24rem] truncate text-sm md:table-cell">{push.body.pt}</TableCell>
                    <TableCell className="hidden sm:table-cell">
                      {rule ? (
                        <Badge variant="info" className="font-normal">
                          {rule}
                        </Badge>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <Switch checked={push.enabled} onCheckedChange={(enabled) => savePush(push.kind, { enabled })} aria-label={`${push.label} ativo`} />
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      <ChevronRight className="size-4" />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>

      <SystemPushSheet push={editing} onClose={() => setEditing(null)} />
    </>
  );
}

function SystemPushSheet({ push, onClose }: { push: SystemPush | null; onClose: () => void }) {
  const { savePush } = usePlatform();
  const [title, setTitle] = useState<LocalizedText>({ pt: '', es: '', en: '' });
  const [body, setBody] = useState<LocalizedText>({ pt: '', es: '', en: '' });
  const [daysBefore, setDaysBefore] = useState('');
  const [enabled, setEnabled] = useState(true);

  useEffect(() => {
    if (!push) return;
    setTitle(push.title);
    setBody(push.body);
    setDaysBefore(push.rule?.daysBefore ? String(push.rule.daysBefore) : '');
    setEnabled(push.enabled);
  }, [push]);

  if (!push) return <Sheet open={false} />;

  const hasRule = push.rule !== undefined;
  const days = Number(daysBefore);
  const ruleInvalid = hasRule && (!Number.isInteger(days) || days < 1 || days > 90);

  const save = () => {
    savePush(push.kind, {
      title,
      body,
      enabled,
      ...(hasRule ? { rule: { ...push.rule, daysBefore: days } } : {}),
    });
    toast.success(`${push.label} salvo.`);
    onClose();
  };

  return (
    <Sheet open onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="sm:w-[36rem] sm:max-w-[36rem]">
        <SheetHeader>
          <SheetTitle className="flex flex-wrap items-center gap-2">
            {push.label}
            <Badge variant={enabled ? 'success' : 'secondary'}>{enabled ? 'Ativo' : 'Desligado'}</Badge>
          </SheetTitle>
          <p className="text-sm text-muted-foreground">{push.trigger}</p>
        </SheetHeader>

        <div className="space-y-5">
          <div className="flex items-center justify-between gap-3 rounded-xl border border-border/60 px-3 py-2">
            <Label htmlFor={`enabled-${push.kind}`} className="font-normal">
              Enviar este push
            </Label>
            <Switch id={`enabled-${push.kind}`} checked={enabled} onCheckedChange={setEnabled} />
          </div>

          {hasRule ? (
            <div className="space-y-1.5">
              <Label htmlFor={`days-${push.kind}`}>Dias antes de expirar</Label>
              <Input
                id={`days-${push.kind}`}
                inputMode="numeric"
                className="w-28"
                value={daysBefore}
                onChange={(e) => setDaysBefore(e.target.value.replace(/[^\d]/g, ''))}
                aria-invalid={ruleInvalid || undefined}
              />
              {ruleInvalid ? <p className="text-xs text-destructive">Entre 1 e 90.</p> : null}
            </div>
          ) : null}

          <LocalizedFields id={`title-${push.kind}`} label="Título" value={title} onChange={setTitle} />
          <LocalizedFields id={`body-${push.kind}`} label="Texto" value={body} onChange={setBody} multiline />

          <div className="space-y-1.5">
            <Label>Prévia</Label>
            <CampaignPhoneMockup
              title={fillVariables(title.pt, VARIABLE_SAMPLES) || push.label}
              message={fillVariables(body.pt, VARIABLE_SAMPLES)}
              channel="push"
            />
          </div>

          <PushTestForm message={fillVariables(body.pt, VARIABLE_SAMPLES)} />

          <div className="space-y-1.5">
            <Label>Variáveis</Label>
            <VariableChips variables={push.variables} />
          </div>

          <p className="text-xs text-muted-foreground">Legado: {push.legacy}</p>

          <div className="flex justify-end">
            <Button type="button" onClick={save} disabled={ruleInvalid}>
              Salvar
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
